// Listing page used by /services and /features (kind="service" | "feature").
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowUpRight, Sparkles } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import { SERVICES, FEATURES } from "@/constants/cosecCatalog";

export default function CatalogListPage({ kind = "service" }) {
  const list = kind === "feature" ? FEATURES : SERVICES;
  const isFeature = kind === "feature";

  return (
    <SiteLayout>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-60 pointer-events-none" />
        <div className="container-cv relative pt-14 md:pt-24 pb-12 max-w-4xl">
          <span className="pill"><Sparkles className="h-3.5 w-3.5 text-accent" /> {isFeature ? `${list.length} modules` : `${list.length} services`}</span>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.04]">
            {isFeature ? "Every CoSec App module," : "Every CoSec service,"}{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-accent">in one place.</span>
              <span className="absolute left-0 bottom-1 h-3 w-full bg-accent/30 -z-0 rounded-sm" />
            </span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
            {isFeature
              ? "Deep-dive into each module that powers CoSec App's UK company secretarial workflows."
              : "Filing, formation, share work, registers and the full Cosec function, pick the service you need."}
          </p>
        </div>
      </section>

      <section className="pb-20 md:pb-28">
        <div className="container-cv">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {list.map((e) => (
              <Link key={e.slug} to={`/${e.slug}`} className="group rounded-2xl border border-border bg-white overflow-hidden hover:-translate-y-1 hover:shadow-md transition-all">
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={e.image} alt={e.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="h-9 w-9 grid place-items-center rounded-lg bg-primary/10 text-primary"><e.Icon className="h-4 w-4" /></div>
                    <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-accent transition-colors" />
                  </div>
                  <h3 className="mt-3 font-display text-lg font-bold text-primary leading-snug group-hover:text-accent transition-colors">{e.title} <span className="text-accent">{e.highlight}</span></h3>
                  <p className="mt-1.5 text-sm text-muted-foreground line-clamp-2 leading-relaxed">{e.subhead}</p>
                  {e.pricing?.from && (
                    <div className="mt-3 text-[11px] font-bold uppercase tracking-wider text-accent">From {e.pricing.from}</div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-[0.07] pointer-events-none" />
        <div className="container-cv relative grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.04]">
              Not sure where to start?{" "}
              <span className="text-accent">Talk to us.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Button asChild className="h-12 px-7 bg-accent hover:bg-accent/90 text-primary font-bold">
              <Link to="/contact">Book a 20-minute call <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
